/**
 * @file applicationService.js
 * @description Application data access layer. Handles submitting applications,
 *   loading applicant pipelines, status transitions, hire confirmation, and
 *   placement notifications. Enforces the allowed status transition graph.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module lib/applicationService
 */
import { supabase } from "./supabase";
import { createNotification } from "./notificationService";
import { haversine } from "./marketReach";
import { trackEvent, EVENTS } from "./eventService";
import { displayCaseCode } from "../theme";
import { loadCaseLabels, attachCaseLabels } from "./caseLabelService";
import { logCaughtError } from "./errorService";

const NOTIFY_APPLICATION_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/notify-agency-application`;
const NOTIFY_STATUS_URL      = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/notify-application-status`;

// Fire-and-forget email to the THERAPIST on a high-signal pipeline move
// (offered / declined / placement / case_filled), alongside the in-app
// notification. notify-application-status has verify_jwt: true, so we attach
// the agency's bearer token. Never awaited, never throws — email must never
// block or break a status change. `payload` is { application_id, kind } for
// per-candidate kinds, or { case_id, exclude_application_id, kind } for the
// case_filled fan-out.
function _emailApplicationStatus(payload) {
  if (!payload?.kind) return;
  try {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.access_token) return;
      fetch(NOTIFY_STATUS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${session.access_token}` },
        body: JSON.stringify(payload),
      }).catch(err => console.warn(`[applicationStatus email:${payload.kind}] failed (non-fatal):`, err?.message));
    }).catch(() => {});
  } catch { /* never block the flow on email */ }
}

export const APPLICATION_STATUSES = Object.freeze([
  "pending",
  "reviewing",
  "interview",
  "offered",
  "hired",
  "declined",
  "withdrawn",
]);

const ALLOWED_STATUS_TRANSITIONS = Object.freeze({
  pending:   ["reviewing", "interview", "offered", "declined", "withdrawn", "closed"],
  reviewing: ["interview", "offered", "declined", "withdrawn", "closed"],
  interview: ["offered", "declined", "withdrawn", "hired", "closed"],
  offered:   ["hired", "declined", "withdrawn", "closed"],
  closed:    [],
  hired:     [],
  // Terminal states can only be REOPENED back to the top of the pipeline
  // ("Reopen" sends UI "new" → DB "pending"). Everything else from a terminal
  // state stays blocked — so e.g. declined→reviewing is still invalid.
  declined:  ["pending"],
  withdrawn: ["pending"],
});

/**
 * @description Returns true if the given status is in the allowed APPLICATION_STATUSES set.
 * @param {string} status - Application status string
 * @returns {boolean}
 */
export function isValidApplicationStatus(status) {
  return APPLICATION_STATUSES.includes(status);
}

/**
 * @description Returns true if transitioning from currentStatus to nextStatus is permitted
 *   by the allowed transition graph, or if both statuses are equal (no-op).
 * @param {string} currentStatus - Current application status
 * @param {string} nextStatus - Proposed next status
 * @returns {boolean}
 */
export function canTransitionApplicationStatus(currentStatus, nextStatus) {
  if (!isValidApplicationStatus(currentStatus) || !isValidApplicationStatus(nextStatus)) return false;
  if (currentStatus === nextStatus) return true;
  return (ALLOWED_STATUS_TRANSITIONS[currentStatus] || []).includes(nextStatus);
}

function assertValidApplicationStatus(status) {
  if (!isValidApplicationStatus(status)) {
    throw new Error(`Invalid application status: ${status}`);
  }
}

/**
 * @description Throws a typed `case_paused` error when the case underlying
 *   an application is moderation-flagged (cases.flagged = true). Called
 *   from the terminal-outcome endpoints (declined, hired, confirmHire) to
 *   keep server-side enforcement honest even if a stale UI bypasses the
 *   client-side disable. Non-terminal status moves and messaging stay
 *   unaffected per the David UX call.
 *
 *   `caseIdHint` short-circuits the lookup when the caller already has
 *   the case_id (e.g. confirmHire receives it as an option). Otherwise
 *   joins via applications → cases by application id.
 *
 * @param {string} applicationId
 * @param {string} action - For error context: "declined" | "hired" | "confirm_hire"
 * @param {string} [caseIdHint]
 * @throws {Error & { code: "case_paused", action: string }}
 */
async function assertCaseNotPaused(applicationId, action, caseIdHint) {
  let caseId = caseIdHint || null;
  if (!caseId) {
    const { data: app } = await supabase
      .from("applications")
      .select("case_id")
      .eq("id", applicationId)
      .maybeSingle();
    caseId = app?.case_id || null;
  }
  if (!caseId) return; // can't check; let the row-level update fail naturally

  const { data: row } = await supabase
    .from("cases")
    .select("flagged")
    .eq("id", caseId)
    .maybeSingle();

  if (row?.flagged) {
    const err = new Error(
      `Case is paused by moderation; cannot ${action.replace("_", " ")} while review is pending.`
    );
    err.code   = "case_paused";
    err.action = action;
    throw err;
  }
}

/**
 * @description Submits a therapist's application to a case. Deduplicates against
 *   existing applications, fires a notify-agency-application edge function call,
 *   and creates an in-app notification for the agency.
 * @param {string} caseId - Target case UUID
 * @param {string} therapistId - Applying therapist's user ID
 * @param {string} [coverNote] - Optional cover note from the therapist
 * @param {{employment?:string, availDays?:string[], hours?:string}} [applied]
 *   Per-application intent from the apply form (may differ from the therapist's
 *   default profile — e.g. available all week generally but only M/W/F for THIS
 *   case). Persisted so the agency sees what was committed for this case.
 * @returns {Promise<Object>} The created application row
 */
export async function submitApplication(caseId, therapistId, coverNote, applied = {}) {
  // Check for existing application to avoid duplicates
  const { data: existing } = await supabase
    .from("applications")
    .select("id")
    .eq("case_id", caseId)
    .eq("therapist_id", therapistId)
    .maybeSingle();

  if (existing) throw new Error("You have already applied to this case.");

  const { data, error } = await supabase
    .from("applications")
    .insert({
      case_id: caseId,
      therapist_id: therapistId,
      status: "pending",
      cover_note: coverNote || null,
      applied_employment:    applied.employment || null,
      applied_avail_days:    Array.isArray(applied.availDays)    && applied.availDays.length    ? applied.availDays    : null,
      applied_avail_periods: Array.isArray(applied.availPeriods) && applied.availPeriods.length ? applied.availPeriods : null,
      applied_hours:         applied.hours || null,
    })
    .select()
    .single();

  if (error) {
    // Server rule (block_rejected_credential_application): a rejected or revoked credential cannot apply. Expected
    // refusal, not logged; the apply form shows the same reason before submit.
    if (String(error.message || "").includes("credential_rejected")) {
      throw new Error("Your credential could not be verified. Update it in your profile before applying.");
    }
    // Server rule (block_test_cross_side): test accounts and real accounts never apply to each other. Expected refusal.
    if (String(error.message || "").includes("test_account_mismatch")) {
      throw new Error("Test accounts can only apply to openings from test agencies.");
    }
    // 23505 = unique violation: a double submit lost the race, which the UI already explains.
    if (error.code !== "23505") logCaughtError(error, "application_submit_failed", { caseId, step: "insert" });
    throw error;
  }

  // Direct-invite dedup (Slice 3A) — when the BEFORE-INSERT trigger has
  // attributed this application to a paid invite (data.invite_id is set),
  // the DB-side trigger notify_agency_on_invite_application has already
  // queued a more useful notification for the agency ("Alex Rivera accepted
  // your invite"). Suppress the generic "new application received" pings
  // below so the agency hears about this event exactly once.
  if (data.invite_id) {
    return data;
  }

  // Fire-and-forget: notify agency of new application (never awaited, never throws).
  // notify-agency-application has verify_jwt: true, so we must include the bearer token.
  try {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.access_token) return;
      fetch(NOTIFY_APPLICATION_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ case_id: caseId, therapist_id: therapistId }),
      }).catch(err => console.warn("[submitApplication] Edge function notify failed (non-fatal):", err?.message));
    }).catch(err => console.warn("[submitApplication] getSession failed (non-fatal):", err?.message));
  } catch {}

  // In-app notification for the agency (shows in the bell)
  try {
    // The applicant may read the case it just applied to; get_case_labels, not
    // a direct cases read (a provider has no table access to cases).
    const caseData = (await loadCaseLabels([caseId]))[caseId] || null;

    if (caseData?.agency_id) {
      const location = caseData.city || caseData.zip || "";
      // Fetch therapist display name so the notification reads "Alex Rivera applied…"
      // rather than "A therapist applied…". Failure falls back to the anonymous body.
      // NOTE: a supabase query builder is only thenable (no .catch), so chaining
      // .catch() here threw a TypeError that the outer try swallowed, and the
      // agency's "New application received" bell notification was never created.
      let tProfile = null;
      try {
        const { data } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", therapistId)
          .maybeSingle();
        tProfile = data;
      } catch { /* anonymous body below */ }
      const therapistName = tProfile?.full_name || "A provider";
      createNotification({
        user_id:    caseData.agency_id,
        type:       "new_applicant",
        title:      "New application received",
        body:       `${therapistName} applied to your ${caseData.type || "RBT"} case${caseData.case_code ? ` (${caseData.case_code})` : ""}${location ? ` in ${location}` : ""}. Tap to review their profile.`,
        link_data:  {
          view:          "dashboard",
          tab:           "pipeline",
          applicationId: data.id,   // deep-link: open this specific applicant card
          caseId:        caseId,    // deep-link: pre-select this listing in the pipeline
          caseCode:      caseData.case_code || null,
        },
        dedupe_key: `new_applicant_${caseId}_${therapistId}`,
        category:   "new_applicants",
      }).catch(err => console.warn("[submitApplication] Agency notification failed (non-fatal):", err?.message));
    }
  } catch (err) {
    console.warn("[submitApplication] In-app notification setup failed (non-fatal):", err?.message);
  }

  return data;
}

/**
 * Ids of the cases this provider has applied to (any status). Feeds the "Applied" marks in Find Cases (card, map pin,
 * case window). Never throws: on failure the marks are simply missing, and the unique (case_id, therapist_id) index
 * still stops a second application.
 * @param {string} therapistId
 * @returns {Promise<Set<string>>}
 */
export async function loadMyAppliedCaseIds(therapistId) {
  if (!therapistId) return new Set();
  const { data, error } = await supabase.from("applications").select("case_id").eq("therapist_id", therapistId);
  if (error) {
    logCaughtError(error, "applications_load_failed", { step: "applied_case_ids" });
    return new Set();
  }
  return new Set((data || []).map((r) => r.case_id).filter(Boolean));
}

// ── Load therapist's own applications (with case details) ──────────────────
export async function loadMyApplications(therapistId) {
  const { data, error } = await supabase
    .from("applications")
    .select(`
      id, case_id, therapist_id, status, cover_note, created_at, updated_at,
      hired_at, start_date, confirmed_hired, confirmed_hired_at, placement_notes,
      offer_terms, offer_response, offer_responded_at
    `)
    .eq("therapist_id", therapistId)
    .order("created_at", { ascending: false });

  if (error) {
    logCaughtError(error, "applications_load_failed", { step: "my_applications" });
    throw error;
  }
  // Case facts come from get_case_labels (the applicant may always see a case it
  // applied to), not a `case:cases(...)` embed: a provider cannot read the cases
  // table. flagged_reason is deliberately absent; the screen only needs `flagged`.
  const withCases = await attachCaseLabels(data || [], "case");
  return withCases.map(dbToMyApplication);
}

/**
 * @description Counts pending agency invites for a therapist. Drives the
 *   numeric badge on the Best Matches navigation so therapists notice new
 *   personal invitations as soon as they land. Uses head: true (no rows
 *   returned, just the count) — cheap to call repeatedly.
 * @param {string} therapistId - The therapist's Supabase user ID
 * @returns {Promise<number>} 0 on error or no invites; otherwise the count
 */
export async function loadPendingInviteCount(therapistId) {
  if (!therapistId) return 0;
  const { count, error } = await supabase
    .from("agency_invites")
    .select("id", { count: "exact", head: true })
    .eq("therapist_id", therapistId)
    .eq("status", "pending");
  if (error) return 0;
  return count || 0;
}

// ── Load applicants for an agency's cases ──────────────────────────────────
// RLS ensures agency only sees applications for their own cases.
export async function loadCaseApplicants(agencyId) {
  // 1. Load applications with case info. Paginate in pages of 1000 so large
  //    agencies don't silently lose applicants past a hard cap (was .limit(500),
  //    which dropped rows beyond 500 with no indication — David go-live
  //    fast-follow). A safety ceiling guards against runaway egress.
  const APP_SELECT = `
      id, case_id, therapist_id, status, cover_note, created_at, updated_at,
      status_changed_at,
      hired_at, start_date, confirmed_hired, confirmed_hired_at, placement_notes,
      invite_id, offer_terms, offer_response, offer_responded_at,
      applied_employment, applied_avail_days, applied_avail_periods, applied_hours,
      case:cases(
        id, case_code, type, listing_kind, position_details, employment, setting, age_range, age_min, age_max, hours, pay_range,
        urgency, skills, zip, lat, lng, agency_name, dba_name, client_initials, status
      )
    `;
  const PAGE = 1000;
  const MAX_ROWS = 5000; // safety ceiling — beyond this, true server-side paging is warranted
  let apps = [];
  for (let from = 0; from < MAX_ROWS; from += PAGE) {
    const { data, error: appErr } = await supabase
      .from("applications")
      .select(APP_SELECT)
      .order("created_at", { ascending: false })
      .range(from, from + PAGE - 1);
    if (appErr) {
      logCaughtError(appErr, "applicants_load_failed", { step: "agency_applicants" });
      throw appErr;
    }
    if (!data || data.length === 0) break;
    apps = apps.concat(data);
    if (data.length < PAGE) break; // last page
  }
  if (apps.length === 0) return [];

  // 2. Load therapist profiles for these applicants
  const therapistIds = [...new Set(apps.map(a => a.therapist_id))];

  const [{ data: profiles }, { data: tProfiles }] = await Promise.all([
    supabase
      .from("profiles")
      // deleted_at surfaces an "account closed" flag on the applicant
      // object — used by the agency UI to gate actions (Reopen, Message,
      // etc.) on rows whose underlying user has gone through the
      // deletion lifecycle. The applicant's identity is anonymized in
      // public surfaces at that point, but the application row may
      // still be sitting in the agency's queue or Archived bucket.
      // NOTE: avatar_url intentionally NOT selected — it's a base64 data-URL
      // duplicate of therapist_profiles.photo_url (written together), so
      // pulling it here doubled image egress across the applicant list for
      // zero benefit. photoUrl in dbToApplicant uses photo_url as primary.
      .select("id, full_name, deleted_at")
      .in("id", therapistIds),
    supabase
      .from("therapist_profiles")
      // bio (summary), cover_letter, resume_url ARE rendered — in the full
      // applicant profile (TherapistProfileModal "Presentation" tab: Summary /
      // Intro Letter / Resume), which reads straight from this loader's result
      // (it does no fetch of its own). They're small text/URL fields, so they
      // stay. The real egress hog (base64 photo) is now a Storage URL. Only the
      // redundant base64 avatar_url (a duplicate of photo_url) stays dropped.
      // MED-R5-6: sex excluded (protected attribute).
      .select("user_id, credential, verified, verification_status, experience, credentialed_year, credentialed_date, state, zip, city, lat, lng, max_distance, skills, settings, age_groups, availability, employment, min_hours, max_hours, languages, certifications, payer_enrollments, behavior_comfort, photo_url, bio, cover_letter, resume_url, resume_path, expiration_date, settings_json, bcaba_details, licensed_states, open_to_relocation, nppes_license_states, nppes_verified")
      .in("user_id", therapistIds),
  ]);

  const profileMap = Object.fromEntries((profiles || []).map(p => [p.id, p]));
  const tProfileMap = Object.fromEntries((tProfiles || []).map(p => [p.user_id, p]));

  // 3. Agency-internal decline reasons for the declined/withdrawn rows. RLS
  //    keeps this table agency-only, so it never leaks to the therapist.
  const declinedIds = apps.filter(a => a.status === "declined" || a.status === "withdrawn").map(a => a.id);
  let reasonByApp = {};
  if (declinedIds.length) {
    const { data: reasons } = await supabase
      .from("application_decline_reasons")
      .select("application_id, reason")
      .in("application_id", declinedIds);
    reasonByApp = Object.fromEntries((reasons || []).map(r => [r.application_id, r.reason]));
  }

  // 4. This agency's own licensure attestations, keyed by therapist. RLS scopes
  //    the table to the calling agency, so this only returns our confirmations.
  const confByTherapist = {};
  {
    const { data: confs } = await supabase
      .from("licensure_confirmations")
      .select("therapist_id, state, confirmed_at")
      .in("therapist_id", therapistIds);
    for (const c of (confs || [])) {
      (confByTherapist[c.therapist_id] ||= []).push({ state: c.state, confirmedAt: c.confirmed_at });
    }
  }

  return apps.map(app => {
    const a = dbToApplicant(app, profileMap[app.therapist_id], tProfileMap[app.therapist_id]);
    a.declineReason = reasonByApp[app.id] || null;
    a.licensureConfirmations = confByTherapist[app.therapist_id] || [];
    return a;
  });
}

// Record (or update) the agency-internal decline reason for an application. Lives
// in a dedicated agency-private table (NOT on applications, which is therapist-
// readable). Stores the canonical reason key. Fire-and-forget — never blocks the
// decline; failures are swallowed by the caller.
export async function recordDeclineReason(applicationId, caseId, agencyId, reasonKey) {
  if (!applicationId || !caseId || !reasonKey) return;
  const { error } = await supabase
    .from("application_decline_reasons")
    .upsert(
      { application_id: applicationId, case_id: caseId, agency_id: agencyId, reason: reasonKey },
      { onConflict: "application_id" }
    );
  if (error) {
    logCaughtError(error, "decline_reason_save_failed", { applicationId, caseId });
    throw error;
  }
}

// ── Update application status (agency action) ─────────────────────────────
export async function updateApplicationStatus(applicationId, newStatus, options = {}) {
  assertValidApplicationStatus(newStatus);

  const { currentStatus = null, skipTransitionCheck = false, agencyName, caseType } = options;
  if (!skipTransitionCheck && currentStatus && !canTransitionApplicationStatus(currentStatus, newStatus)) {
    throw new Error(`Invalid application status transition: ${currentStatus} → ${newStatus}`);
  }

  // Moderation gate — terminal outcomes (hired, declined) are blocked
  // while the case is paused. Non-terminal transitions (reviewing,
  // interview, offered) stay open so the agency can keep processing
  // the in-flight pipeline; messaging is unaffected. Per David's UX
  // call: hire/reject affect a real person and shouldn't happen while
  // the listing's standing is unresolved.
  if (newStatus === "declined" || newStatus === "hired") {
    await assertCaseNotPaused(applicationId, newStatus);
  }

  // Offer-acceptance lock — once an offer has been SENT (status = "offered"),
  // the agency cannot advance the candidate to "hired" until the candidate has
  // ACCEPTED (offer_response = "accepted"). This prevents an agency from firing
  // off an offer and immediately marking the person hired before they've even
  // seen it (the exact issue that surfaced with ABA Core → Gleidy). Enforced
  // again at the DB layer by the trg_offer_must_be_accepted trigger.
  if (newStatus === "hired") {
    const { data: cur } = await supabase
      .from("applications")
      .select("status, offer_response")
      .eq("id", applicationId)
      .maybeSingle();
    if (cur?.status === "offered" && cur.offer_response !== "accepted") {
      const err = new Error("The candidate must accept the offer before you can confirm the hire.");
      err.code = "offer_not_accepted";
      throw err;
    }
  }

  const { error } = await supabase
    .from("applications")
    .update({ status: newStatus })
    .eq("id", applicationId);

  if (error) {
    logCaughtError(error, "application_status_update_failed", { applicationId, stage: newStatus });
    throw error;
  }

  // Notify the therapist about meaningful status changes. Resolve the agency's
  // DISPLAY NAME (DBA, falling back to legal) and the case role straight from the
  // DB — so the in-app copy always reads "The ABACore Therapy has sent you an
  // offer for a RBT position", matching the email — rather than the legal entity
  // name or a blank role (the caller no longer needs to pass agencyName/caseType).
  const NOTIFY_STATUSES = ["reviewing", "interview", "offered", "declined"];
  // Declared outside the try block below so the audit event further down
  // (which needs case_code for Admin Logs) can still read it even if this
  // whole notification lookup is skipped or throws — caseCodeForEvent stays
  // null in that case rather than throwing a ReferenceError.
  let caseCodeForEvent = null;
  if (NOTIFY_STATUSES.includes(newStatus)) {
    try {
      const { data: app } = await supabase
        .from("applications")
        .select("therapist_id, case:cases(case_code, type, agency_name, dba_name)")
        .eq("id", applicationId)
        .single();
      const c = app?.case || {};
      caseCodeForEvent = c.case_code || null;
      const who  = c.dba_name || c.agency_name || agencyName || "An agency";
      const role = c.type || caseType || "";
      const roleWord = role ? `${role} ` : "";
      const codeTag  = c.case_code ? ` (case ${c.case_code})` : "";   // internal case id, e.g. "RBT-1434"
      const STATUS_NOTIFICATIONS = {
        reviewing: { title: "Application under review", body: `${who} is reviewing your ${roleWord}application${codeTag}.` },
        interview: { title: "Interview requested",       body: `${who} wants to schedule an interview for your ${roleWord}application${codeTag}. Check your Applications tab.` },
        offered:   { title: "You received an offer!",    body: `${who} has sent you an offer${role ? ` for a ${role} position` : ""}${codeTag}. Review it in your Applications tab.` },
        declined:  { title: "Application update",         body: `${who} has decided not to move forward with your ${roleWord}application${codeTag}. Check your messages for details.` },
      };
      const notif = STATUS_NOTIFICATIONS[newStatus];
      if (app?.therapist_id && notif) {
        createNotification({
          user_id:    app.therapist_id,
          type:       `application_${newStatus}`,
          title:      notif.title,
          body:       notif.body,
          link_data:  { view: "applications", caseCode: c.case_code || null },
          dedupe_key: `app_status_${applicationId}_${newStatus}`,
          category:   "application_updates",
        }).catch(err => console.warn(`[updateApplicationStatus] Notification for '${newStatus}' failed (non-fatal):`, err?.message));
      }
    } catch (err) {
      console.warn("[updateApplicationStatus] Status notification setup failed (non-fatal):", err?.message);
    }
  }

  // Email for the transitions a provider must hear about even when they are not in the app: In Review (owner
  // 2026-10-30, sets a 24 to 48 hour wait), offered and declined. "interview" stays in-app only: the scheduler's own
  // invite email (notify-interview) is the real signal, so emailing the bare stage here would double up.
  if (newStatus === "reviewing" || newStatus === "offered" || newStatus === "declined") {
    _emailApplicationStatus({ application_id: applicationId, kind: newStatus });
  }

  // Audit event — actor is the current session user (the agency making the
  // call). Every pipeline stage between "applied" and "hired" previously had
  // NO trace in the events table at all (only case_journal, which is scoped
  // into a single case's own timeline, not surfaced anywhere in Admin) — the
  // Admin Activity log was structurally blind to reviewing/interview/offered/
  // declined, not just filtering them out. Same fire-and-forget pattern as
  // confirmHire's HIRE_CONFIRMED.
  const STATUS_EVENTS = {
    reviewing: EVENTS.APP_REVIEWING,
    interview: EVENTS.APP_INTERVIEW,
    offered:   EVENTS.APP_OFFERED,
    declined:  EVENTS.APP_DECLINED,
  };
  if (STATUS_EVENTS[newStatus]) {
    supabase.auth.getSession().then(({ data: s }) => {
      const uid = s?.session?.user?.id;
      if (uid) trackEvent(uid, STATUS_EVENTS[newStatus], { application_id: applicationId, case_code: caseCodeForEvent });
    }).catch(() => {});
  }
}

// ── Mark application as hired ─────────────────────────────────────────────
export async function updateApplicationHired(applicationId, options = {}) {
  return updateApplicationStatus(applicationId, "hired", options);
}

// ── Confirm placement (agency explicitly confirms the person started) ─────────
// Sets confirmed_hired = true, records the start date, inserts a celebratory
// notification for the therapist. Safe to call after updateApplicationStatus("hired").
//
// { startDate }   — ISO date string (YYYY-MM-DD), optional
// { notes }       — free-text placement notes, optional
// { therapistId } — UUID of the placed therapist (for notification)
// { agencyName }  — displayed in the notification body
// { caseType }    — e.g. "RBT", used in notification copy
// { caseId }      — UUID of the case; when provided, marks case as filled and
//                   notifies all other non-terminal applicants on the same case
export async function confirmHire(applicationId, { startDate, notes, therapistId, agencyName, caseType, caseId } = {}) {
  // Moderation gate — confirmHire is the terminal "hired & confirmed"
  // step. Block when the case is paused. Same UX rationale as the
  // updateApplicationStatus gate: terminal outcomes against a real
  // person while the case's standing is unresolved are not OK.
  await assertCaseNotPaused(applicationId, "confirm_hire", caseId);

  const now = new Date().toISOString();

  const { error } = await supabase
    .from("applications")
    .update({
      confirmed_hired:    true,
      confirmed_hired_at: now,
      start_date:         startDate  || null,
      placement_notes:    notes      || null,
    })
    .eq("id", applicationId);

  if (error) {
    logCaughtError(error, "hire_confirm_failed", { applicationId, step: "update" });
    throw error;
  }

  // Audit event — actor is the current session user (the agency). getSession()
  // is a local read (no network). Fire-and-forget — never blocks the hire.
  supabase.auth.getSession().then(({ data: s }) => {
    const uid = s?.session?.user?.id;
    if (uid) trackEvent(uid, EVENTS.HIRE_CONFIRMED, { case_id: caseId || null, application_id: applicationId });
  }).catch((e) => console.warn("[confirmHire] hire audit event failed (non-fatal):", e?.message || e));

  // Mark the case as filled so it no longer appears as active in the marketplace,
  // and release the posting slot so the agency's slot meter reflects reality.
  if (caseId) {
    const { error: caseErr } = await supabase.from("cases").update({ status: "filled", slot_released: true }).eq("id", caseId);
    // Best-effort — placement is already confirmed; case status update failure is non-critical
    if (caseErr) console.warn("[confirmHire] Failed to mark case as filled:", caseErr.message);
  }

  // Fetch case metadata once: case_code for deep-link chips, plus the agency's
  // DISPLAY NAME (DBA, falling back to legal) and role so placement / case-filled
  // copy matches the email. Best-effort; nulls are safe for older rows.
  let caseCode = null, whoDisplay = agencyName || null, roleDisplay = caseType || null;
  if (caseId) {
    const { data: caseRow } = await supabase.from("cases").select("case_code, type, agency_name, dba_name").eq("id", caseId).single();
    caseCode    = caseRow?.case_code || null;
    whoDisplay  = caseRow?.dba_name || caseRow?.agency_name || agencyName || null;
    roleDisplay = caseRow?.type     || caseType   || null;
  }

  // Send in-app notification to the hired therapist
  if (therapistId) {
    const startText = startDate
      ? ` Your start date is ${new Date(startDate + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}.`
      : "";
    const roleText = roleDisplay ? ` for a ${roleDisplay} position` : "";
    // Notification is best-effort — placement is already committed to DB.
    // Swallow any insert failure so it does not surface as a hire error to the agency.
    await createNotification({
      user_id:   therapistId,
      type:      "placement",
      title:     "Congratulations, you've been placed!",
      body:      `${whoDisplay || "An agency"} has confirmed your placement${roleText}${caseCode ? ` (case ${caseCode})` : ""}.${startText} Check your Applications tab for details.`,
      link_data: { view: "applications", applicationId, caseCode },
    }).catch(err => console.warn("[confirmHire] Placement notification failed (non-fatal):", err?.message));
    _emailApplicationStatus({ application_id: applicationId, kind: "placement" });
  }

  // NOTE: the other applicants are NOT notified here. confirmHire places the
  // winner; the losing applicants are notified exactly once — in-app AND by
  // email — when the agency clears the pipeline via closePendingApplications
  // (the "close remaining applicants" step that follows a hire). Notifying
  // here as well produced a duplicate in-app notice ~18s apart, and the old
  // case_filled email fan-out re-queried "still-active" applicants, so it
  // could race a near-simultaneous decline and silently email no one.
}

/**
 * Record an agency's affirmative attestation that it independently verified a
 * provider's state license for a (cross-state remote) placement. This — not the
 * platform — is the authoritative licensure trust signal. Point-in-time record.
 *
 * @param {Object} p
 * @param {string} p.agencyId      — the attesting agency (= auth.uid())
 * @param {string} p.therapistId
 * @param {string} [p.caseId]
 * @param {string} p.state         — the license state being attested
 * @param {string} [p.note]
 * @returns {Promise<{ok:boolean, error?:string}>}
 */
export async function recordLicensureConfirmation({ agencyId, therapistId, caseId = null, state, note = null }) {
  if (!agencyId || !therapistId || !state) return { ok: false, error: "missing args" };
  const { error } = await supabase.from("licensure_confirmations").insert({
    agency_id:    agencyId,
    therapist_id: therapistId,
    case_id:      caseId,
    state,
    note,
    confirmed_by: agencyId,
  });
  if (error) {
    console.warn("[recordLicensureConfirmation] failed:", error.message);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

/**
 * Close out the remaining OPEN applicants on a case after a hire. A single-
 * placement ABA case has one seat — once someone is placed the other pipeline
 * candidates are moot — so this declines them in one pass and sends each the
 * agency's chosen (or a default) message. The hired application is excluded.
 *
 * @param {string} caseId
 * @param {Object} [opts]
 * @param {string} [opts.excludeApplicationId] - the hired app to leave alone
 * @param {string} [opts.message] - agency's decline message (falls back to default)
 * @param {string} [opts.agencyName]
 * @returns {Promise<{count:number}>}
 */
export async function closePendingApplications(caseId, { excludeApplicationId = null, message = null, agencyName = null } = {}) {
  if (!caseId) return { count: 0 };

  // Fetch case_code + agency display name once (best-effort; null is safe).
  let caseCode = null, whoDisplay = agencyName || null;
  const { data: caseRow } = await supabase.from("cases").select("case_code, agency_name, dba_name").eq("id", caseId).single();
  caseCode   = caseRow?.case_code || null;
  whoDisplay = caseRow?.dba_name || caseRow?.agency_name || agencyName || null;

  let q = supabase
    .from("applications")
    .select("id, therapist_id")
    .eq("case_id", caseId)
    .not("status", "in", '("hired","withdrawn","declined","closed")');
  if (excludeApplicationId) q = q.neq("id", excludeApplicationId);
  const { data: open, error } = await q;
  if (error) {
    logCaughtError(error, "application_close_pending_failed", { caseId, step: "select" });
    throw error;
  }
  if (!open || open.length === 0) return { count: 0 };

  const ids = open.map(a => a.id);
  const now = new Date().toISOString();
  const { error: updErr } = await supabase
    .from("applications")
    .update({ status: "declined", status_changed_at: now })
    .in("id", ids);
  if (updErr) {
    logCaughtError(updErr, "application_close_pending_failed", { caseId, step: "update" });
    throw updErr;
  }

  const body = (message && message.trim())
    ? message.trim()
    : `${whoDisplay || "The agency"} has filled this position${caseCode ? ` (case ${caseCode})` : ""}. Thank you for applying, we'd welcome you for future openings.`;
  for (const a of open) {
    await createNotification({
      user_id:    a.therapist_id,
      type:       "application_declined",
      title:      "Application update",
      body,
      link_data:  { view: "applications", caseCode },
      dedupe_key: `app_closed_${a.id}`,
      category:   "applications",
    }).catch(err => console.warn("[closePendingApplications] notify failed (non-fatal):", err?.message));
    // Email the same outcome, reusing the per-candidate "declined" template.
    // Fire-and-forget and keyed by application_id (not a "still-active"
    // re-query), so it is safe to send AFTER the batch decline above — no race
    // with the status flip. This also makes batch-decline consistent with a
    // single manual decline, which already sends this email.
    _emailApplicationStatus({ application_id: a.id, kind: "declined" });
  }

  // The single seat is taken and the agency just cleared the rest of the
  // pipeline — flip the case to "filled" so it leaves the active marketplace
  // and stops drawing new applicants. Safety net for cases whose hire was
  // recorded outside confirmHire() (which already marks the case filled).
  // Guarded to cases that actually have a hire and aren't already
  // filled/archived, so this never silently closes an open case.
  let caseFilled = false;
  const { data: hiredRows } = await supabase
    .from("applications").select("id").eq("case_id", caseId).eq("status", "hired").limit(1);
  if (hiredRows && hiredRows.length > 0) {
    const { error: caseErr } = await supabase
      .from("cases").update({ status: "filled" })
      .eq("id", caseId)
      .not("status", "in", '("filled","archived")');
    if (caseErr) console.warn("[closePendingApplications] mark filled failed (non-fatal):", caseErr.message);
    else caseFilled = true;
  }

  return { count: open.length, caseFilled };
}

// ── Listing-edit helpers ───────────────────────────────────────────────────

// Returns the number of active applicants for a specific case.
// Used by CasePosting to determine whether to lock high-risk fields.
export async function getApplicantCountForCase(caseId) {
  const { count, error } = await supabase
    .from("applications")
    .select("id", { count: "exact", head: true })
    .eq("case_id", caseId)
    .not("status", "in", '("withdrawn","declined","closed")');
  if (error) return 0;
  return count ?? 0;
}

// Returns therapist user IDs for all active applicants on a case.
// Used to send notifications when medium-risk fields are edited.
export async function getApplicantUserIdsForCase(caseId) {
  const { data, error } = await supabase
    .from("applications")
    .select("therapist_id")
    .eq("case_id", caseId)
    .not("status", "in", '("withdrawn","declined","hired","closed")');
  if (error) return [];
  return (data || []).map(r => r.therapist_id).filter(Boolean);
}

// Agency notes about an applicant live in the notes engine (lib/notes, case_journal with application_id) since
// 2026-10-18. applications.agency_notes is no longer read or written; it is dropped in a later migration.

export async function saveOfferTerms(applicationId, terms) {
  const { error } = await supabase
    .from("applications")
    .update({ offer_terms: terms })
    .eq("id", applicationId);
  if (error) throw error;
}

/**
 * @description Therapist responds to an extended offer (David's #3 — closes the
 *   "extended → accepted/declined" loop). Records the response as an overlay on
 *   the 'offered' status (status itself flips to hired only when the agency
 *   confirms). Notifies the agency in-app + by email so they can confirm the
 *   hire (accept) or move on (decline).
 * @param {string} applicationId
 * @param {string} therapistId - the responding therapist (authorization)
 * @param {"accept"|"decline"} action
 * @param {{reason?:string}} [opts]
 * @returns {Promise<{ok:boolean, error?:string}>}
 */
export async function respondToOffer(applicationId, therapistId, action, { reason } = {}) {
  if (!applicationId || !therapistId) return { ok: false, error: "missing_args" };
  if (action !== "accept" && action !== "decline") return { ok: false, error: "invalid_action" };

  // Load + authorize: must be this therapist's application, currently offered,
  // and not already responded to.
  const { data: app, error: loadErr } = await supabase
    .from("applications")
    .select("id, therapist_id, case_id, status, offer_response")
    .eq("id", applicationId)
    .single();
  if (loadErr || !app) return { ok: false, error: "not_found" };
  if (app.therapist_id !== therapistId) return { ok: false, error: "forbidden" };
  if (app.status !== "offered") return { ok: false, error: "not_offered" };
  if (app.offer_response) return { ok: false, error: "already_responded" };
  // The case this offer is for (agency id + display name, role, case code). A
  // provider reads it through get_case_labels, not a cases embed; the applicant
  // is always allowed to see the case they applied to, whatever its status.
  app.case = (await loadCaseLabels([app.case_id]))[app.case_id] || null;

  const response = action === "accept" ? "accepted" : "declined";
  // .select("id") is load-bearing, not decorative: Postgres RLS silently
  // EXCLUDES rows a policy doesn't grant rather than raising an error, so an
  // update that matches zero rows still comes back with updErr === null. A
  // real production bug lived here for exactly that reason — every therapist
  // response silently no-op'd against a table with no UPDATE policy for
  // therapists at all, while this function went on to notify + email the
  // agency as if it had succeeded. Checking rows-affected, not just for an
  // error, is what actually catches that class of failure.
  const { data: updated, error: updErr } = await supabase
    .from("applications")
    .update({
      offer_response:       response,
      offer_responded_at:   new Date().toISOString(),
      offer_decline_reason: action === "decline" ? (reason || null) : null,
    })
    .eq("id", applicationId)
    .eq("therapist_id", therapistId)   // belt + suspenders authz at the row level
    .select("id");
  if (updErr) {
    logCaughtError(updErr, "offer_response_failed", { applicationId, step: action });
    return { ok: false, error: updErr.message };
  }
  if (!updated || updated.length === 0) {
    // A policy excluded the row: nothing was saved and the user is told so. Never expected.
    logCaughtError(new Error("offer response not persisted"), "offer_response_failed", { applicationId, step: action, reason: "not_persisted" });
    return { ok: false, error: "not_persisted" };
  }

  // Audit event — actor is the responding therapist. Fire-and-forget, same
  // pattern as confirmHire's HIRE_CONFIRMED below; feeds the Admin Activity
  // log once offer_accepted/offer_declined are added to LIFECYCLE_EVENTS.
  trackEvent(therapistId, action === "accept" ? EVENTS.OFFER_ACCEPTED : EVENTS.OFFER_DECLINED, {
    application_id: applicationId,
    case_id:        app.case_id,
    case_code:      app.case?.case_code || null,
  });

  const agencyId   = app.case?.agency_id || null;
  const caseType   = app.case?.type || "";
  const caseCode   = app.case?.case_code || null;

  // In-app notification to the agency.
  if (agencyId) {
    const meta = action === "accept"
      ? { type: "offer_accepted", title: "Offer accepted!",
          body: `A candidate accepted your offer for the ${caseType || ""} case${caseCode ? ` (${caseCode})` : ""}. Confirm the hire to lock in the placement.` }
      : { type: "offer_declined", title: "Offer declined",
          body: `A candidate declined your offer for the ${caseType || ""} case${caseCode ? ` (${caseCode})` : ""}. You can extend an offer to another applicant.` };
    createNotification({
      user_id:    agencyId,
      type:       meta.type,
      title:      meta.title,
      body:       meta.body,
      link_data:  { view: "dashboard", tab: "pipeline", applicationId, caseId: app.case_id, caseCode },
      dedupe_key: `offer_response_${applicationId}_${response}`,
      category:   "new_applicants",
    }).catch(err => console.warn("[respondToOffer] Agency notification failed (non-fatal):", err?.message));
  }

  // Email the agency (rides the same edge function; caller = therapist).
  _emailApplicationStatus({ application_id: applicationId, kind: action === "accept" ? "offer_accepted" : "offer_declined" });

  // The case-journal entry (agency-visible audit of the outcome) is written by
  // the journal_offer_response_on_change trigger (SECURITY DEFINER) — the
  // therapist's session can't write the agency-owned case_journal directly.

  return { ok: true };
}

// ── Transforms ─────────────────────────────────────────────────────────────

export function dbToMyApplication(row) {
  const c = row.case || {};
  const created = row.created_at ? new Date(row.created_at) : new Date();
  const updated = row.updated_at ? new Date(row.updated_at) : created;

  // Map DB status → UI status keys used by STATUS_META / STATUS_TO_STAGE in MyApplications.
  // "hired" in the DB only reveals itself as the celebratory "hired" state to the therapist
  // once the agency has explicitly confirmed the placement (confirmed_hired = true).
  // Before that it stays at "offered" so we don't show a false "Position Filled 🎉" prematurely.
  const uiStatus = (() => {
    if (row.status === "hired") return row.confirmed_hired ? "hired" : "offered";
    const map = {
      pending:    "pending",
      reviewing:  "reviewing",
      interview:  "interview",
      offer_sent: "offered",
      offered:    "offered",
      declined:   "declined",
      withdrawn:  "withdrawn",
    };
    return map[row.status] || "pending";
  })();

  return {
    id:              row.id,
    caseId:          row.case_id,
    caseCode:        c.case_code || "",
    agency:          c.dba_name || c.agency_name || "Agency",
    type:            c.type || "RBT",
    listingKind:     c.listing_kind || "case",
    positionDetails: c.listing_kind === "position" ? c.position_details || null : null,
    employment:      c.employment || "W2",
    setting:         c.setting || "Home",
    ageRange:        c.age_range || "",
    // Ages served on a position (agesServed() reads these); a case keeps its widened range string.
    ageMin:          c.age_min ?? null,
    ageMax:          c.age_max ?? null,
    hours:           c.hours || 0,
    payRange:        c.pay_range || "",
    serviceTime:     c.service_time || "",
    clientSex:       c.client_sex || "Not Specified",
    urgency:         c.urgency || "medium",
    zip:             c.zip || "",
    status:          uiStatus,
    confirmedHired:  row.confirmed_hired || false,
    startDate:       row.start_date || null,
    hiredAt:         row.hired_at || null,
    appliedAt:       created.toISOString().slice(0, 10),
    lastUpdate:      updated.toISOString().slice(0, 10),
    agencyId:        c.agency_id || null,
    offerTerms:      row.offer_terms || null,
    offerResponse:   row.offer_response || null,       // accepted | declined | null
    offerRespondedAt: row.offer_responded_at || null,
    messages:        [],
    // Moderation flag — when true, the agency can't currently respond.
    // MyApplications surfaces a paused banner on the card so the
    // therapist isn't left waiting in the dark. Reason text is for
    // admin/agency only — therapists see only the neutral "paused"
    // language per the David UX spec.
    flagged:         !!c.flagged,
    flaggedAt:       c.flagged_at || null,
  };
}

// Per-stage "aging" SLA in days — how long an applicant may sit in a pipeline
// stage before the queue flags it stalled. Terminal stages (hired/declined) have
// no clock. Keyed on the UI status (see statusMap in dbToApplicant).
export const STAGE_SLA_DAYS = {
  new:            3,
  reviewing:      5,
  interview_sent: 7,
  offer_sent:     5,
};

// Aging signal for an applicant row: { days, sla, stalled }. stalled = sat in a
// non-terminal stage at/over its SLA. Deleted accounts never stall (already inert).
export function applicantStageAging(applicant) {
  const days = applicant?.daysInStage ?? 0;
  const sla = STAGE_SLA_DAYS[applicant?.status] ?? null;
  const stalled = sla != null && !applicant?.accountDeleted && days >= sla;
  return { days, sla, stalled };
}

export function dbToApplicant(app, profile, tProfile) {
  const c = app.case || {};
  const tp = tProfile || {};
  const p = profile || {};
  const fullName = p.full_name || "Unnamed applicant";
  const initials = fullName.split(/\s+/).filter(Boolean).map(w => w[0]).join("").toUpperCase().slice(0, 2);
  const created = app.created_at ? new Date(app.created_at) : new Date();
  const daysAgo = Math.floor((Date.now() - created.getTime()) / 86_400_000);
  // Time in the CURRENT pipeline stage — from status_changed_at (stamped by a DB
  // trigger only on real status changes), falling back to created_at for rows
  // that predate the column. Drives the "Nd in stage" + stalled signal.
  const stageSince = app.status_changed_at ? new Date(app.status_changed_at) : created;
  const daysInStage = Math.max(0, Math.floor((Date.now() - stageSince.getTime()) / 86_400_000));

  // Map DB status to pipeline status
  // DB allows: pending, reviewing, interview, offered, hired, declined, withdrawn
  const statusMap = {
    pending:    "new",
    reviewing:  "reviewing",
    interview:  "interview_sent",
    offered:    "offer_sent",
    hired:      "hired",
    declined:   "declined",
    withdrawn:  "declined",
    closed:     "declined",
  };

  // Compute haversine distance between therapist and case location
  const tLat = parseFloat(tp.lat) || null;
  const tLng = parseFloat(tp.lng) || null;
  const cLat = parseFloat(c.lat) || null;
  const cLng = parseFloat(c.lng) || null;
  const distanceMi = (tLat && tLng && cLat && cLng)
    ? Math.round(haversine(tLat, tLng, cLat, cLng))
    : null;

  return {
    id:             app.id,
    listingId:      app.case_id,
    therapistId:    app.therapist_id,
    name:           fullName,
    initials,
    photoUrl:       tp.photo_url || p.avatar_url || "",
    type:           c.type || "RBT",
    credential:         tp.credential || "",
    verified:           tp.verified || false,
    verificationStatus: tp.verification_status || "unverified",
    // Licensure (remote): self-attested states + NPPES-sourced license states.
    licensedStates:     tp.licensed_states     || [],
    // A preference, not contact info: safe to show alongside the applicant's distance.
    openToRelocation:   tp.open_to_relocation   || false,
    nppesLicenseStates: tp.nppes_license_states || [],
    nppesVerified:      tp.nppes_verified       || false,
    experience:     tp.experience || 0,
    credentialedYear: tp.credentialed_year ?? null,
    credentialedDate: tp.credentialed_date ? String(tp.credentialed_date).slice(0, 7) : null,
    city:           tp.city  || "",
    state:          tp.state || "",
    zip:            tp.zip   || "",
    distanceMi,
    maxDistance:    String(tp.max_distance ?? 20),
    employment:     tp.employment || [],
    skills:         tp.skills || [],
    settings:       tp.settings || [],
    ageGroups:      tp.age_groups || [],
    availability:   tp.availability || [],
    hoursWanted:    tp.min_hours && tp.max_hours ? `${tp.min_hours}–${tp.max_hours}` : tp.max_hours ? `up to ${tp.max_hours}` : "",
    minHours:       tp.min_hours  || null,
    maxHours:       tp.max_hours  || null,
    appliedDaysAgo: daysAgo,
    // ISO timestamp from the DB row — kept alongside appliedDaysAgo so
    // surfaces that need finer-grain relative time (Insights activity
    // ticker: "12 min ago") can compute it without rounding to days.
    appliedAt:     created.toISOString(),
    daysInStage,
    stageSince:    stageSince.toISOString(),
    status:         statusMap[app.status] || "new",
    dbStatus:       app.status,
    // Offer-stage response (David #3): null | "accepted" | "declined".
    // Overlay on the "offered" status — drives the agency's "confirm hire" vs
    // "move on" cue without a separate pipeline column.
    offerResponse:    app.offer_response || null,
    offerRespondedAt: app.offer_responded_at || null,
    // Per-application intent from the apply form (may differ from profile
    // defaults). Surfaced to the agency so "available all week generally but
    // only M/W/F for THIS case" isn't silently lost.
    appliedEmployment:    app.applied_employment    || null,
    appliedAvailDays:     app.applied_avail_days    || null,
    appliedAvailPeriods:  app.applied_avail_periods || null,
    appliedHours:         app.applied_hours         || null,
    // Boolean flag set when the underlying user account is no longer
    // active (anonymization has run). Surfaces in the agency UI to
    // disable pipeline actions that would silently no-op against a
    // user who isn't there — e.g. reopening a withdrawn application,
    // sending a message, or trying to advance them to Interview.
    accountDeleted: !!p?.deleted_at,
    bgCheck:        (() => {
      const raw = (tp.settings_json || {}).backgroundCheck
               || (tp.bcaba_details || {}).backgroundCheck
               || "";
      return !!raw && raw !== "Not Yet Completed" && raw !== "No";
    })(),
    bgCheckRaw:     (tp.settings_json || {}).backgroundCheck || "",
    coverLetter:    tp.cover_letter || "",
    expirationDate: tp.expiration_date || "",
    coverNote:      app.cover_note || "",
    languages:      tp.languages || [],
    certifications:   tp.certifications    || [],
    payerEnrollments: tp.payer_enrollments || [],
    behaviorComfort:  tp.behavior_comfort  || [],
    summary:          tp.bio || "",
    workExperience:   (tp.settings_json || {}).workExperience || "",
    // Profile default time of day (Morning / Afternoon / Evening / Flexible); the fit engine prefers the
    // appliedAvailPeriods the provider chose for this case.
    availabilityPeriods: (tp.settings_json || {}).availabilityPeriods || [],
    resumeUrl:        tp.resume_url || "",
    resumePath:       tp.resume_path || "",
    // Direct Invite — Slice 1. Non-NULL invite_id means this application
    // came from a paid agency invite. UI uses this to render the
    // "Invited" badge in the applicant pipeline.
    inviteId:       app.invite_id || null,
    invited:        !!app.invite_id,
    // Hire confirmation fields
    confirmedHired:    app.confirmed_hired    || false,
    confirmedHiredAt:  app.confirmed_hired_at || null,
    hiredAt:           app.hired_at           || null,
    startDate:         app.start_date         || null,
    placementNotes:    app.placement_notes    || null,
    // Terms the agency proposed when sending the offer ({ startDate, payRate,
    // employment, hoursPerWeek, scheduleNotes, message }). The placement modal
    // pre-fills its start date from offerTerms.startDate so the agency isn't
    // asked to re-enter the date the candidate already accepted.
    offerTerms:        app.offer_terms        || null,
    // Case metadata
    caseCode:       displayCaseCode(c.case_code) || "",
    caseType:       c.type,
    listingKind:    c.listing_kind || "case",
    positionDetails: c.listing_kind === "position" ? c.position_details || null : null,
    caseSetting:    c.setting,
    caseAgeRange:   c.age_range,
    casePayRange:   c.pay_range,
    caseSkills:     c.skills || [],
    caseUrgency:    c.urgency,
    caseZip:        c.zip,
    caseAgencyName: c.dba_name || c.agency_name || "",
    // Joined-case fields for cross-case surfaces — present even when the case is
    // archived/filled (and thus absent from the dashboard's active listings).
    caseClientInitials: c.client_initials || "",
    caseStatus:         c.status || "",
  };
}

/**
 * Subscribe to new application INSERTs for a given agency's cases.
 *
 * The `applications` table is already part of the `supabase_realtime` publication.
 * We can't filter by a list in a Supabase Realtime filter, so we subscribe to all
 * new applications and check the case ownership client-side using a Set of listing IDs.
 *
 * @param {() => string[]} getListingIds  — called at event time to get current listing IDs
 * @param {(row: object) => void} onNew   — called when a new application for one of those cases arrives
 * @returns {() => void}  unsubscribe function
 */
export function subscribeToApplicationChanges(getListingIds, onNew) {
  const channel = supabase
    .channel("applications:new:agency")
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "applications" },
      (payload) => {
        const row = payload.new;
        // applications keys the case as case_id (NOT listing_id) — the old
        // filter referenced a non-existent column, so onNew never fired and the
        // live applicant feed was dead (agencies only saw new applicants on a
        // full reload). The getListingIds() set holds case ids.
        if (!row || !row.case_id) return;
        const ids = getListingIds();
        if (ids.includes(row.case_id)) onNew(row);
      }
    )
    .subscribe();

  return () => { supabase.removeChannel(channel); };
}

/**
 * Agency asks an applicant for a résumé (server: request_resume, migration 20261119090000). Posts a résumé request
 * in the conversation and rings the provider's inbox. Never throws.
 * @param {string} applicationId
 * @returns {Promise<{ ok: boolean, reason?: string }>} reason: already_requested | already_has_resume |
 *   application_closed | not_found | not_signed_in | error
 */
export async function requestResume(applicationId) {
  try {
    const { data, error } = await supabase.rpc("request_resume", { p_application_id: applicationId });
    if (error) return { ok: false, reason: "error" };
    return data?.ok ? { ok: true } : { ok: false, reason: data?.reason || "error" };
  } catch {
    return { ok: false, reason: "error" };
  }
}
